import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Formik, type FormikConfig, type FormikValues } from 'formik';
import type { ReactNode } from 'react';

import { tCommon, tOrder } from '@frontend/locales';

import { DialogForm } from './dialog-form.component';

export interface FilterDialogShellProps<T extends FormikValues> {
  open: boolean;
  onClose(): void;
  /** Form values to start from, already mapped from the persisted filter. */
  initialValues: T;
  validationSchema?: FormikConfig<T>['validationSchema'];
  /** Called with the form values, then the dialog closes. */
  onSubmit(values: T): void;
  /** The form values "Clear all" resets to. Without it there is no "Clear all". */
  getClearedValues?(): T;
  /** Paper width in px, capped at 90vw. */
  width?: number;
  /** "Save" keeps the submit disabled until something has changed. */
  submitAction?: 'apply' | 'save';
  /** The filter fields, rendered inside the Formik form. */
  children: ReactNode;
}

/**
 * The dialog, form and action row shared by the filter dialogs. Each page only
 * maps its filter to form values and back, and renders its fields.
 */
export const FilterDialogShell = <T extends FormikValues>({
  open,
  onClose,
  initialValues,
  validationSchema,
  onSubmit,
  getClearedValues,
  width = 560,
  submitAction = 'apply',
  children,
}: FilterDialogShellProps<T>) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    slotProps={{ paper: { sx: { maxWidth: '90vw', width } } }}
  >
    {/* Mounted only while open, so every opening starts from the current filter */}
    {open && (
      <Formik<T>
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange
        onSubmit={(values) => {
          onSubmit(values);
          onClose();
        }}
      >
        {(formikProps) => (
          <DialogForm>
            <DialogTitle>{tOrder.headers.filter()}</DialogTitle>

            <DialogContent>{children}</DialogContent>

            <DialogActions>
              {getClearedValues && (
                <Button
                  variant="text"
                  color="neutral"
                  sx={{ mr: 'auto' }}
                  onClick={() => formikProps.setValues(getClearedValues())}
                >
                  {tCommon.actions.clearAll()}
                </Button>
              )}

              <Button
                variant="outlined"
                color="neutral"
                sx={{ minWidth: 128 }}
                onClick={onClose}
              >
                {tCommon.actions.back()}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="success"
                sx={{ minWidth: 128 }}
                disabled={
                  !formikProps.isValid ||
                  (submitAction === 'save' && !formikProps.dirty)
                }
              >
                {submitAction === 'save'
                  ? tCommon.actions.save()
                  : tCommon.actions.apply()}
              </Button>
            </DialogActions>
          </DialogForm>
        )}
      </Formik>
    )}
  </Dialog>
);
